import axios from 'axios';
import {
  DRIVER_PLANNING,
  DRIVER_MISSION_DETAIL,
} from '@/services/constants/storageKeys';
import { getInfo } from '@/services/users';
import { setItemSafe } from '@/services/utils/localStorageUtils';

let planningData = null;
// Checks whether data about user planning is still stored in the application
let storedPlanning = window.localStorage.getItem(DRIVER_PLANNING);
if (storedPlanning) {
  planningData = JSON.parse(storedPlanning);
}

let missionDetailData = {};
// We check if wa have the detail of mission is still stored in the application
let storedMissionDetail = window.localStorage.getItem(DRIVER_MISSION_DETAIL);
if (storedMissionDetail) {
  missionDetailData = JSON.parse(storedMissionDetail);
}

/**
 * Save planning data into local storage
 */
function saveData() {
  setItemSafe(DRIVER_PLANNING, JSON.stringify(planningData));
  setItemSafe(DRIVER_MISSION_DETAIL, JSON.stringify(missionDetailData));
}

/**
 * Clear data from the driver's schedule
 */
function clearMission() {
  window.localStorage.clear();
  planningData = null;
  missionDetailData = {};
}

/**
 * Retrieval of the connected driver's schedule
 * @returns {Promise<*|{}>} // All reservations, deliveries, transports and absences of this driver
 */
async function fetchPlanning(month = null) {
  let userInfo = null;
  try {
    userInfo = await getInfo();
  } catch (e) {
    console.error('Unable to get user info', e);
  }

  // Useful for outsourcing, where there is no real user
  if (!userInfo) return;

  const config = {
    headers: {
      Authorization: `Bearer ${userInfo.token}`,
    },
  };

  if (month) {
    config.params = { month };
  }

  const response = await axios.get('/driver/planning', config);
  const { headers } = response;

  // store needed headers in local storage
  // don't update values if not needed to avoid useless write

  let existing = window.localStorage.getItem('postMaxSize');
  if (
    headers['x-post-max-size'] &&
    (!existing || existing !== headers['x-post-max-size'])
  ) {
    setItemSafe('postMaxSize', headers['x-post-max-size']);
  }

  existing = window.localStorage.getItem('uploadMaxFileSize');
  if (
    headers['x-upload-max-filesize'] &&
    (!existing || existing !== headers['x-upload-max-filesize'])
  ) {
    setItemSafe('uploadMaxFileSize', headers['x-upload-max-filesize']);
  }

  existing = window.localStorage.getItem('displayMachineReports');
  if (existing !== headers['x-display-machine-reports']) {
    setItemSafe('displayMachineReports', headers['x-display-machine-reports']);
  }

  existing = window.localStorage.getItem('timeReportsTimerMode');
  if (existing !== headers['x-time-reports-timer-mode']) {
    setItemSafe('timeReportsTimerMode', headers['x-time-reports-timer-mode']);
  }

  // if month, directly return data without saving
  if (month) {
    return response.data;
  }

  planningData = response.data;
  saveData();
}

async function fetchMission(typeMission, idRide, idMissionType, idCoupon) {
  const { token } = await getInfo();

  let endpoint = '/driver/';

  if (typeMission === 'reservation') {
    endpoint += 'reservation/';
    endpoint += idCoupon ? idCoupon : idRide + '/' + idMissionType;
  } else {
    endpoint += 'transport/';
    endpoint += idCoupon ? idCoupon : idRide + '/' + idMissionType;
  }

  const response = await axios.get(endpoint, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      append_days_elements: true,
    },
  });

  replaceMission(response.data);
  saveData();
  return response.data;
}

async function fetchMissionOutsourcing(typeMission, outsourcingToken) {
  // Use an absolute URL to override default `pwa-api` baseURL
  let outsourcingEndpoint = `${process.env.VUE_APP_API_URL_OUTSOURCING}/driver/`;

  if (typeMission === 'reservation') {
    outsourcingEndpoint += 'reservation/';
  } else {
    outsourcingEndpoint += 'transport/';
  }
  outsourcingEndpoint += `${outsourcingToken}`;

  const response = await axios.get(outsourcingEndpoint);
  return response.data;
}

async function replaceMission(newMission) {
  for (const mission in planningData) {
    if (planningData[mission].id === newMission.id) {
      planningData[mission] = newMission;
      return;
    }
  }
}

/**
 *
 * @returns {Promise<*|{}>} // Get the last informations of mission
 */
async function fetchDetailMission(typeMission, idMissionType, idMissionItem, idCoupon) {
  const { token } = await getInfo();

  let endpoint = '/driver/';

  if (typeMission === 'reservation') {
    endpoint += 'reservation/detail/';
    endpoint += idCoupon ? 'coupon/' + idCoupon : idMissionType;
  } else {
    endpoint += 'transport/detail/';
    endpoint += idCoupon ? idCoupon : + idMissionType + '/' + idMissionItem;
  }

  const response = await axios.get(endpoint, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (typeMission === 'reservation') {
    missionDetailData[idMissionType] = response.data;
  } else {
    missionDetailData[idMissionType + '-' + idMissionItem] = response.data;
  }
  saveData();
}

async function fetchDetailMissionOutsourcing(typeMission, outsourcingToken) {
  // Use an absolute URL to override default `pwa-api` baseURL
  let outsourcingEndpoint = `${process.env.VUE_APP_API_URL_OUTSOURCING}/driver/`;

  if (typeMission === 'reservation') {
    outsourcingEndpoint += 'reservation/';
  } else {
    outsourcingEndpoint += 'transport/';
  }
  outsourcingEndpoint += `detail/${outsourcingToken}`;

  const response = await axios.get(outsourcingEndpoint);
  return response.data;
}

/**
 * Recovery of the planning
 */
async function getPlanning() {
  if (!planningData) {
    await fetchPlanning();
  }

  return planningData;
}

/**
 * Returns the details of the targeted mission,
 * makes a call to the server if the details of the mission are not known
 */
async function getDetailMission(typeMission, idMissionType, idMissionItem, idCoupon = null) {
  if ((
      (typeMission === 'reservation' && !missionDetailData[idMissionType])
      || ((typeMission === 'transfert' || typeMission === 'livraison')
          && !missionDetailData[idMissionType + '-' + idMissionItem]))
      || idMissionType === 'null'
  ) {
    await fetchDetailMission(typeMission, idMissionType, idMissionItem, idCoupon);
  }

  if (typeMission === 'reservation') {
    return missionDetailData[idMissionType];
  }
  // if (typeMission === ('transfert' || 'livraison')) {
  return missionDetailData[idMissionType + '-' + idMissionItem];
}

/**
 * Force refresh of the schedule
 */
async function forceRefreshPlanning(month = null) {
  if (month) {
    return fetchPlanning(month);
  }

  await fetchPlanning();
  return planningData;
}

async function forceRefreshDetailMission(
  typeMission,
  idMissionType,
  idMissionItem,
) {
  await fetchDetailMission(typeMission, idMissionType, idMissionItem);
  if (typeMission === 'reservation') {
    return missionDetailData[idMissionType];
  }
  // if (typeMission === ('transfert' || 'livraison')) {
  return missionDetailData[idMissionType + '-' + idMissionItem];
}

/**
 * Recovery of a mission according to its id
 */
function getMissionOrder(idMissionType, idRide, typeMission, idCoupon = null) {
  return fetchMission(
    typeMission,
    idRide,
    idMissionType,
    idCoupon,
  );
}

export {
  clearMission,
  getPlanning,
  getDetailMission,
  getMissionOrder,
  forceRefreshPlanning,
  forceRefreshDetailMission,
  fetchMissionOutsourcing,
  fetchDetailMissionOutsourcing,
};
